/**
 * Newsletter Birth Date Handler
 * Manages date formatting and validation for birth date input
 */

class NewsletterBirthDate {
  constructor() {
    this.init();
  }

  init() {
    // Find all birth date inputs
    const birthDateInputs = document.querySelectorAll('[data-birth-date-input]');
    
    birthDateInputs.forEach(input => {
      this.setupInput(input);
    });
  }

  setupInput(input) {
    // Add input event listener for formatting
    input.addEventListener('input', (e) => {
      this.formatDateInput(e.target);
    });

    // Add keydown event listener for better UX
    input.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });

    // Add blur event listener for validation
    input.addEventListener('blur', (e) => {
      this.validateDate(e.target);
    });

    // Add form submit event listener
    const form = input.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        this.handleFormSubmit(e, input);
      });
    }
  }

  formatDateInput(input) {
    let value = input.value.replace(/\D/g, ''); // Remove non-digits
    
    // Limit to 8 digits (DDMMYYYY)
    if (value.length > 8) {
      value = value.substring(0, 8);
    }

    // Format as DD/MM/YYYY
    if (value.length >= 3) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 6) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }

    input.value = value;
  }

  handleKeydown(e) {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  }

  validateDate(input) {
    const value = input.value.trim();
    
    // Se il campo è vuoto, lascia che la validazione nativa HTML gestisca il required
    if (!value) {
      return true;
    }

    // Check format DD/MM/YYYY
    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = value.match(dateRegex);
    
    if (!match) {
      this.showError(input, 'Inserisci una data di nascita valida (GG/MM/AAAA)');
      return false;
    }

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validate year (not in the future, not too old)
    const currentYear = new Date().getFullYear();
    if (year > currentYear || year < 1900) {
      this.showError(input, 'Inserisci un anno valido');
      return false;
    }

    // Validate month
    if (month < 1 || month > 12) {
      this.showError(input, 'Inserisci un mese valido (01-12)');
      return false;
    }

    // Validate day
    if (day < 1 || day > 31) {
      this.showError(input, 'Inserisci un giorno valido (01-31)');
      return false;
    }

    // Create date object and validate
    const date = new Date(year, month - 1, day);
    
    // Check if date is valid (handles leap years, month boundaries, etc.)
    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
      this.showError(input, 'Inserisci una data di nascita valida');
      return false;
    }

    // Check if date is not in the future
    if (date > new Date()) {
      this.showError(input, 'La data di nascita non può essere nel futuro');
      return false;
    }

    this.clearError(input);
    return true;
  }

  showError(input, message) {
    this.clearError(input);
    
    input.setAttribute('aria-invalid', 'true');
    input.classList.add('field__input--error');
    
    const errorId = input.id + '-error';
    const errorElement = document.createElement('small');
    errorElement.id = errorId;
    errorElement.className = 'newsletter-form__message form__message';
    errorElement.setAttribute('aria-live', 'polite');
    errorElement.innerHTML = `
      <svg class="icon icon-error" aria-hidden="true" focusable="false">
        <use href="#icon-error"></use>
      </svg>
      ${message}
    `;
    
    input.setAttribute('aria-describedby', errorId);
    
    // Insert error message after the .field element
    const fieldElement = input.closest('.field');
    if (fieldElement && fieldElement.parentNode) {
      fieldElement.parentNode.insertBefore(errorElement, fieldElement.nextSibling);
    } else {
      // Fallback: insert after input's parent if .field not found
      input.parentNode.insertBefore(errorElement, input.parentNode.nextSibling);
    }
  }

  clearError(input) {
    input.removeAttribute('aria-invalid');
    input.classList.remove('field__input--error');
    
    const errorId = input.id + '-error';
    const existingError = document.getElementById(errorId);
    if (existingError) {
      existingError.remove();
    }
    
    input.removeAttribute('aria-describedby');
  }

  handleFormSubmit(e, input) {
    // Skip validation if field is empty (optional field)
    const value = input.value.trim();
    if (!value) {
      return true;
    }

    // Only validate if a value is provided
    if (!this.validateDate(input)) {
      e.preventDefault();
      input.focus();
      return false;
    }

    // Add birth date to customer tags
    this.addBirthDateToTags(input);
  }

  addBirthDateToTags(input) {
    const form = input.closest('form');
    const tagsInput = form.querySelector('input[name="contact[tags]"]');
    
    if (tagsInput && input.value) {
      const birthDate = input.value; // DD/MM/YYYY format
      let currentTags = tagsInput.value;
      
      // Remove existing birthdate: tag if present
      currentTags = currentTags.replace(/birthdate:[^,]*/g, '');
      
      // Clean up any double commas
      currentTags = currentTags.replace(/,,+/g, ',');
      currentTags = currentTags.replace(/^,|,$/g, '');
      
      // Convert DD/MM/YYYY to YYYY-MM-DD format
      const [day, month, year] = birthDate.split('/');
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      // Add birth date as a tag in format: birthdate:YYYY-MM-DD
      const newTags = currentTags + (currentTags ? ',' : '') + 'birthdate:' + formattedDate;
      tagsInput.value = newTags;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new NewsletterBirthDate();
});

// Also initialize for dynamically loaded content
document.addEventListener('shopify:section:load', () => {
  new NewsletterBirthDate();
});
